import { Box, ClickAwayListener } from '@mui/material';
import { StyledList } from '@/components/AssetsPage/PopoverList';
import { Children, useRef, useState, useLayoutEffect } from 'react';

const MENU_PADDING = 8;

export const BaseContextMenu = ({
  anchorPosition,
  onClose,
  children,
}: {
  anchorPosition: { left: number; top: number } | undefined;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const menuReference = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState<{ left: number; top: number } | undefined>();
  const hasChildren = Children.toArray(children).length > 0;

  useLayoutEffect(() => {
    if (!anchorPosition || !menuReference.current) {
      setAdjustedPosition(undefined);
      return;
    }

    const menuRect = menuReference.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let { left, top } = anchorPosition;

    if (left + menuRect.width > viewportWidth - MENU_PADDING) {
      left = viewportWidth - menuRect.width - MENU_PADDING;
    }
    if (left < MENU_PADDING) {
      left = MENU_PADDING;
    }

    if (top + menuRect.height > viewportHeight - MENU_PADDING) {
      top = viewportHeight - menuRect.height - MENU_PADDING;
    }
    if (top < MENU_PADDING) {
      top = MENU_PADDING;
    }

    setAdjustedPosition({ left, top });
  }, [anchorPosition]);

  if (!anchorPosition || !hasChildren) return;

  const position = adjustedPosition ?? anchorPosition;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        ref={menuReference}
        sx={theme => ({
          position: 'fixed',
          left: position.left,
          top: position.top,
          zIndex: theme.zIndex.tooltip + 1,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px',
          minWidth: '160px',
          boxShadow: theme.shadows[8],
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          overflow: 'hidden',
        })}
      >
        <StyledList sx={{ padding: 0 }}>{children}</StyledList>
      </Box>
    </ClickAwayListener>
  );
};
